// src/components/auth/ForgotPasswordModal.jsx
import React, { useState } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useModal } from '@/contexts/ModalContext';
import { requestPasswordReset } from '@/services/apiService';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';

/**
 * ============================================================================
 * ForgotPasswordModal Component (AuthLayout Style)
 * ============================================================================
 * 
 * DESIGN:
 * ✅ Matches AuthLayout dark theme (raisin-black background)
 * ✅ Rounded corners (rounded-xl)
 * ✅ Proper spacing and padding
 * ✅ Swan text color on dark background
 * ✅ Primary/Negative button variants
 * ============================================================================
 */

const ForgotPasswordModal = () => {
  const { closeModal } = useModal();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      const errorMsg =
        err?.response?.data?.errors?.email ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        'Failed to send reset email. Please try again.';

      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    closeModal();
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-raisin-black rounded-xl border-none p-8">
        {!success ? (
          <>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-mikado-yellow/10 rounded-lg">
                  <Mail className="text-mikado-yellow" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white font-poppins">
                    Reset Password
                  </h2>
                </div>
              </div>
              <p className="text-sm text-swan/70 mt-2">
                Enter your email to receive reset instructions
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-swan mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full text-gray-900 placeholder:text-gray-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                {/* Cancel button - negative variant */}
                <Button
                  type="button"
                  variant="negative"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                
                {/* Send Reset Link button - primary variant */}
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !email}
                  className="flex-1"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            {/* Success State */}
            <div className="text-center py-2">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/10 rounded-full mb-4">
                <CheckCircle className="text-green-400" size={32} />
              </div>

              <h2 className="text-xl font-bold text-white font-poppins mb-4">
                Check Your Email
              </h2>

              <p className="text-sm text-swan/70 mb-2">
                We've sent password reset instructions to:
              </p>

              <p className="font-medium text-mikado-yellow mb-6 text-base">
                {email}
              </p>

              <div className="bg-stone-brown/30 rounded-lg p-4 mb-6 text-left border border-pebble-brown/20">
                <p className="text-sm font-semibold text-swan mb-3">
                  Next steps:
                </p>
                <ol className="text-sm text-swan/80 space-y-2 list-decimal list-inside">
                  <li>Check your email inbox</li>
                  <li>Click the reset link (valid for 24 hours)</li>
                  <li>Create a new password</li>
                </ol>
              </div>

              {/* Got It button - primary variant */}
              <Button 
                variant="primary" 
                onClick={handleClose} 
                className="w-full"
              >
                Got It
              </Button>

              <p className="text-xs text-swan/50 mt-4">
                Didn't receive the email? Check your spam folder or try again in a few minutes.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;